import React, { useEffect, useState } from 'react';
import {
  normalizeEstimatedValueRange,
  normalizePriceEstimateDistribution,
} from '../utils/formatters';

// Bin accent colors. Badges use translucent fills + theme-aware text so they
// read correctly on the dark cards (and stay legible in light mode).
const BIN_COLORS = [
  { bar: 'bg-positive', dot: 'bg-positive', badge: 'bg-positive/15 text-positive border-positive/30' },
  { bar: 'bg-teal', dot: 'bg-teal', badge: 'bg-teal/15 text-teal border-teal/30' },
  { bar: 'bg-ai', dot: 'bg-ai', badge: 'bg-ai/15 text-ai border-ai/30' },
  { bar: 'bg-warn', dot: 'bg-warn', badge: 'bg-warn/15 text-warn border-warn/30' },
  { bar: 'bg-danger', dot: 'bg-danger', badge: 'bg-danger/15 text-danger border-danger/30' },
];

const toFinitePrice = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyValue = (value) => {
  const numericValue = toFinitePrice(value);
  if (numericValue === null) return 'N/A';
  const hasDecimals = Math.abs(numericValue % 1) > 0;
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: hasDecimals ? 2 : 0,
  });
};

const formatRangeText = (low, high) => {
  const lowValue = toFinitePrice(low);
  const highValue = toFinitePrice(high);
  if (lowValue === null && highValue === null) return 'N/A';
  if (lowValue === null) return `$${formatCurrencyValue(highValue)}`;
  if (highValue === null) return `$${formatCurrencyValue(lowValue)}`;
  if (lowValue === highValue) return `$${formatCurrencyValue(lowValue)}`;
  return `$${formatCurrencyValue(lowValue)} - $${formatCurrencyValue(highValue)}`;
};

const BinRow = ({ entry, index }) => {
  const color = BIN_COLORS[index % BIN_COLORS.length];
  return (
    <div className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${color.dot}`} />
      <span className="text-[10px] flex-1 text-muted tabular-nums">{formatRangeText(entry.low, entry.high)}</span>
      <span className={`text-[10px] rounded border px-1.5 py-0.5 font-semibold ${color.badge}`}>
        {(entry.probability * 100).toFixed(0)}%
      </span>
    </div>
  );
};

const PriceDistributionView = ({
  rangeValue,
  distributionValue,
  maxBins = 4,
  compact = false,
  allowBreakdown = true,
  className = '',
}) => {
  const [showAllBins, setShowAllBins] = useState(false);
  const normalizedDistribution = normalizePriceEstimateDistribution({
    price_estimate_distribution: distributionValue,
  });

  const normalizedRange = normalizeEstimatedValueRange({ estimated_value_range: rangeValue })
    || normalizeEstimatedValueRange(rangeValue)
    || (normalizedDistribution.length > 0
      ? {
          low: Math.min(...normalizedDistribution.map((entry) => entry.low)),
          high: Math.max(...normalizedDistribution.map((entry) => entry.high)),
        }
      : null);

  const rangeLabel = normalizedRange
    ? formatRangeText(normalizedRange.low, normalizedRange.high)
    : 'N/A';

  const safeMaxBins = Math.max(1, maxBins);
  const isExpandable = normalizedDistribution.length > safeMaxBins;
  const displayBins = showAllBins ? normalizedDistribution : normalizedDistribution.slice(0, safeMaxBins);
  const hiddenBinCount = Math.max(0, normalizedDistribution.length - displayBins.length);
  const titleSizeClass = compact ? 'text-[10px]' : 'text-[11px]';
  const valueSizeClass = compact ? 'text-[10px]' : 'text-xs';

  useEffect(() => {
    if (!compact && !isExpandable) {
      setShowAllBins(false);
    }
  }, [compact, isExpandable]);

  const header = (
    <div className="flex items-center justify-between gap-2">
      <span className={`${titleSizeClass} font-semibold uppercase tracking-wide text-muted`}>Price Range</span>
      <span className={`${valueSizeClass} font-semibold text-positive tabular-nums`}>{rangeLabel}</span>
    </div>
  );

  if (normalizedDistribution.length === 0) {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {header}
        <div className={`${valueSizeClass} text-muted`}>Distribution unavailable</div>
      </div>
    );
  }

  // Compact (item-card) mode: Figma look — a slim range track marking the most
  // likely band + a one-line summary, with the full breakdown collapsed.
  if (compact) {
    const topBin = normalizedDistribution.reduce((a, b) => (b.probability > a.probability ? b : a));
    const span = normalizedRange ? (normalizedRange.high - normalizedRange.low) : 0;
    const segLeft = span > 0 ? Math.max(0, ((topBin.low - normalizedRange.low) / span) * 100) : 0;
    const rawWidth = span > 0 ? ((topBin.high - topBin.low) / span) * 100 : 100;
    const segWidth = Math.min(100 - segLeft, Math.max(rawWidth, 8));
    return (
      <div className={`space-y-1 ${className}`}>
        {/* Figma: range low / high flank the track — no "PRICE RANGE" label */}
        <div className="flex items-center justify-between gap-2 text-[10px] text-muted tabular-nums">
          <span>{normalizedRange ? `$${formatCurrencyValue(normalizedRange.low)}` : ''}</span>
          <span>{normalizedRange ? `$${formatCurrencyValue(normalizedRange.high)}` : ''}</span>
        </div>
        <div className="relative w-full overflow-hidden rounded-full bg-ink/20 h-[3px]">
          <div className="absolute top-0 h-full rounded-full bg-positive" style={{ left: `${segLeft}%`, width: `${segWidth}%` }} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] text-muted">
            most likely <span className="font-semibold text-ink tabular-nums">{formatRangeText(topBin.low, topBin.high)}</span>
            {' · '}{(topBin.probability * 100).toFixed(0)}%
          </p>
          {allowBreakdown && normalizedDistribution.length > 1 && (
            <button
              type="button"
              onClick={() => setShowAllBins((previous) => !previous)}
              className="text-[10px] font-medium text-muted hover:text-ink transition-colors"
            >
              {showAllBins ? 'Hide' : 'Breakdown'}
            </button>
          )}
        </div>
        {allowBreakdown && showAllBins && (
          <div className="space-y-1 pt-0.5">
            {normalizedDistribution.map((entry, index) => (
              <BinRow key={`${entry.low}-${entry.high}-${index}`} entry={entry} index={index} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Full mode: multi-segment bar + per-bin list.
  return (
    <div className={`space-y-1.5 ${className}`}>
      {header}
      <div className="flex w-full overflow-hidden rounded-full bg-surface-2 h-2">
        {normalizedDistribution.map((entry, index) => {
          const color = BIN_COLORS[index % BIN_COLORS.length];
          const width = Math.max(0, entry.probability * 100);
          return (
            <div
              key={`${entry.low}-${entry.high}-${index}`}
              className={`${color.bar} h-full`}
              style={{ width: `${width}%` }}
              title={`${formatRangeText(entry.low, entry.high)} • ${(entry.probability * 100).toFixed(1)}%`}
            />
          );
        })}
      </div>
      <div className="space-y-1.5">
        {displayBins.map((entry, index) => (
          <BinRow key={`${entry.low}-${entry.high}-${index}`} entry={entry} index={index} />
        ))}
        {isExpandable && (
          <button
            type="button"
            onClick={() => setShowAllBins((previous) => !previous)}
            className="text-xs text-left font-medium text-muted hover:text-ink transition-colors"
          >
            {showAllBins ? 'Show fewer bins' : `+${hiddenBinCount} additional bins`}
          </button>
        )}
      </div>
    </div>
  );
};

export default PriceDistributionView;
